/**
 * Utilities supporting the post-org-switch refresh flow used by
 * `AuthenticatedLayout.handleSwitchOrganization`.
 *
 * Org-scoped React Query keys do not encode the active organization id, so
 * after switching we clear the cache and let pages refetch under the new
 * JWT. If the route they are on references a resource that doesn't exist in
 * the new org (e.g. `/surveys/<old-org-survey-id>`), the resulting 404 is
 * handled by walking up one segment, repeating once if needed, and finally
 * falling back to the dashboard.
 */

export const POST_SWITCH_FALLBACK_PATH = "/dashboard";

/**
 * Strips trailing slashes (but keeps the leading "/" so the result is
 * always an absolute path). Empty input is normalized to "/".
 */
function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  const trimmed = path.replace(/\/+$/g, "");
  return trimmed.length > 0 ? trimmed : "/";
}

/**
 * Returns the parent route for a given pathname:
 *   /surveys/abc/edit -> /surveys/abc
 *   /surveys/abc      -> /surveys
 *   /surveys          -> POST_SWITCH_FALLBACK_PATH
 *   /                 -> POST_SWITCH_FALLBACK_PATH
 *
 * Query strings and hashes are dropped — after a 404 we are starting fresh
 * for the new organization.
 */
export function walkBackOrFallback(currentPath: string): string {
  const pathOnly = currentPath.split("?")[0].split("#")[0];
  const normalized = normalizePath(pathOnly);

  if (normalized === "/" || normalized === POST_SWITCH_FALLBACK_PATH) {
    return POST_SWITCH_FALLBACK_PATH;
  }

  const segments = normalized.split("/").filter(Boolean);
  if (segments.length <= 1) {
    return POST_SWITCH_FALLBACK_PATH;
  }

  segments.pop();
  return `/${segments.join("/")}`;
}

/**
 * True if the error thrown by an API request looks like a 404. Keeps the
 * 404 detection in one place so the layout doesn't need to import
 * `ApiError` directly.
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { status?: unknown }).status;
  return status === 404;
}
